import React, { useState, useReducer, useEffect } from 'react'
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import SRPForm from '../components/SRPForm';
import client from '../feathers';
import IconButton from '@material-ui/core/IconButton';
import CreateIcon from '@material-ui/icons/AddBox';
import EditIcon from '@material-ui/icons/Edit';
import Switch from '@material-ui/core/Switch';
import useIntl from '../hooks/useIntl';
import TMDataTable from '../lib/Table/TMDataTable';
import TMDialog from '../lib/Dialog/TMDialog';
import { useProgressDialog } from '../context/ProgressDialogProvider';
import { useAlertDialog } from '../context/AlertDialogProvider';
import useSentry from '../hooks/useSentry';

const useStyles = makeStyles((theme) => ({
  hoverButton: {
    color: '#757575',
    '&:hover': {
    	color: theme.palette.primary.main
    }
  },
  inactive: {
    '& td': {
      color: theme.palette.secondary.main,
    }
  },
}));

export default function DriversOverview() {
	
	const [openAdd, setOpenAdd] = useState(false);
	const [openEdit, setOpenEdit] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { showProgressDialog, hideProgressDialog } = useProgressDialog();
  const { showAlert } = useAlertDialog();
  const { enqueueSnackbar } = useSnackbar();
	const { translate } = useIntl();
	const classes = useStyles();
	const logSentry = useSentry();

	const fetchDrivers = async () => {
		const result = await client.service('drivers').find();
    dispatch({ payload: { drivers: result.data } })
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const columns = [
		'Last Name',
		'First Name',
		'Email',
		'Mobile phone',
		'AlfaPass Number',
		'Action',
		{
      name: "isActive",
      options: { filter: false, download: false, display: false, viewColumns: false }
    }, 
	];

	const openEditDriver = async (driver) => {
    dispatch({ payload: {
    	firstName: driver.firstName,
			lastName: driver.lastName,
			email: driver.email,
			phone: driver.phone,
			identification: driver.identification,
			isActive: driver.isActive,
			organizationAddress: driver.organization_address,
			id: driver.id
    }});
    setOpenEdit(true);
  };

	const handleChange = (e) => {
    dispatch({ payload: { 
    	[e.target.name]: (e.target.hasOwnProperty('checked') ? e.target.checked : e.target.value) 
    }})
  };

  const handleAddDriver = async () => {
    let didNotError = true;
    try {
    	showProgressDialog();
    	// validation
      const { isNew } = await client.service('drivers').create(state);
      enqueueSnackbar(translate(isNew ? "driver.created" : "driver.alreadyExists"), { autoHideDuration: 1500 });
    } catch (error) {
      logSentry(error);
    	showAlert(error.message);
    	didNotError = false;
    } finally {
      dispatch({ type: 'RESET' });
      didNotError && await fetchDrivers();
      setOpenAdd(false);
      hideProgressDialog();
    }
	}

	const handleEditDriver = async () => {
    let didNotError = true;
    try {
    	showProgressDialog();
    	// validation
			const data = {
				firstName: state.firstName, 
				lastName: state.lastName, 
				email: state.email, 
				phone: state.phone,
				alfaPass: state.identification, 
				isActive: state.isActive
			};
		  await client.service('drivers').patch(state.id, data);
      enqueueSnackbar("Driver updated succesfully", { autoHideDuration: 1500 });
    } catch (error) {
      logSentry(error);
    	showAlert(error.message);
    	didNotError = false;
    } finally {
      dispatch({ type: 'RESET' });
      didNotError && await fetchDrivers();
      setOpenEdit(false);
      hideProgressDialog();
    }
	}

	const setRowProps = (row, dataIndex) => {
    // last field = isActive
    if(row[row.length-1] === false) return { className: classes.inactive }
  };

	const getData = () => {
		return state.drivers.map(d => [
			d.lastName, 
			d.firstName, 
			d.email, 
			d.phone, 
			d.identification,
			<IconButton className={classes.hoverButton} onClick={() => openEditDriver(d)}><EditIcon/></IconButton>,
			d.isActive
		])
	}

	return (
		<React.Fragment>
			<div className="drivers-table">
				<TMDataTable
		      title={'Drivers'}
		      columns={columns}
		      data={getData()}
		      setRowProps={setRowProps}
		      options={{
			      print: false,
			      download: false,
			      customToolbar: () => 
			      	<IconButton className={classes.hoverButton} onClick={() => setOpenAdd(true)}><CreateIcon/></IconButton>
		      }}
		    />
	    </div>

	    <TMDialog
	      key="add-driver"
	      title={"Add driver"}
	      dialogOpen={openAdd}
	      handleDialogClose={() => setOpenAdd(false)}
	      maxWidth="md"
	      showBottomClose={false}
	    >
	      <SRPForm
	        object={state}
	        handleChange={handleChange}
	        handleSubmit={handleAddDriver}
	        submitLabel={"Add driver"}
	        handleCancel={() => setOpenAdd(false)}
	        cancelLabel={translate("general.cancel")}
	        fields={[
	    			{ field: 'firstName', label: 'First name', required: true }, 
	    			{ field: 'lastName', label: 'Last name', required: true }, 
	    			{ field: 'email', label: 'Email', isEmail: true },
	    			{ field: 'phone', label: 'Phone', type: 'tel' },
	    			{ field: 'identification', label: 'AlfaPass Nbr', required: true },
	        ]}
	      />
	    </TMDialog>

	    <TMDialog
	      key="edit-driver"
	      title={"Edit driver"}
	      dialogOpen={openEdit}
	      handleDialogClose={() => setOpenEdit(false)}
	      maxWidth="md"
	      showBottomClose={false}
	    >
	      <SRPForm
	        object={state}
	        handleChange={handleChange}
	        handleSubmit={handleEditDriver}
	        submitLabel={translate("driver.edit")}
	        handleCancel={() => setOpenEdit(false)}
	        cancelLabel={translate("general.cancel")}
	        fields={[
	    			{ field: 'firstName', label: 'First name', required: true }, 
	    			{ field: 'lastName', label: 'Last name', required: true }, 
	    			{ field: 'email', label: 'Email', isEmail: true },
	    			{ field: 'phone', label: 'Phone', type: 'tel' },
	    			{ field: 'identification', label: 'AlfaPass Nbr', required: true },
            { field: 'isActive', label: 'Active ?', componentType: Switch },
	        ]}
	      />
	    </TMDialog>
		</React.Fragment>
	);
}

const initialState = {
  drivers: [],
  id: undefined,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  identification: '',
  isActive: false,
  organizationAddress: '',
}

const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'RESET':
      return { ...initialState }
    default:
      // = SET
      return { ...state, ...payload }
  }
}
